import { useEffect, useState } from 'react';

/**
 * The `signal` prop is used to restart our interval. Any time a new reference or value is passed for signal our timer will restart.
 * This is useful in a case like the following:
 *   - A unit queue row loads. Correspondingly, this timer will start.
 *   - 30 seconds later a relevant update comes for the row. A cell (which ultimately consumes this hook) displaying number of minutes since some datetime should update (and will show 0M).
 *   - 30 seconds later this interval will run because 60 seconds have elapsed since the hook mounted(assuming there is no signal that has reset the timer).
 * 		We will show the user that 0 minutes have passed since their provided date (specifically, 30 seconds have passed since their provided date).
 *   - 30 seconds later, when we should shift from showing the user "0M" to "1M", nothing happens.
 * 		This is because this hooks timer is elapsing at a time that's not effectively showing by-the-minute updates.
 *   - 30 seconds later, we finally show the user "1M". This is 30 seconds later than was ideal.
 */
type UseNowProps = {
    signal?: unknown;
    interval?: number;
};
export function useNow(props?: UseNowProps): number {
    const [now, setNow] = useState(Date.now());
    const intervalToUse = props?.interval ?? 60000;
    useEffect(() => {
        setNow(Date.now());
        const intervalId = setInterval(() => {
            setNow(Date.now());
        }, 60000);
        return () => {
            return clearInterval(intervalId);
        };
    }, [props?.signal, intervalToUse]);
    return now;
}
